import * as React from 'react';
import { Link, useHistory, useLocation, useParams, useRouteMatch } from 'react-router-dom';
import { createAppRoute, createAppSettingsRouteWithSettingType } from '../helpers/routeHelpers';
import routes from '../routes';
import {
    AppComponentProps,
    AppRouteComponentMatchParams,
    ReleaseRouteComponentMatchParams,
} from '../typescriptHelpers';
import { ArrowLeftOutlined, SettingOutlined, CloudSyncOutlined } from '@ant-design/icons';
import { runwayColors } from './styles/globalStyles';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../reducers/reducers';
import { selectIsAppsDemoModeEnabled } from '../selectors/selectors';
import Button from 'antd/lib/button';
import Popover from 'antd/lib/popover';
import IntegrationRefreshPopover from './IntegrationRefreshPopover';
import { statusNodeForIntegrationStatus } from '../helpers/integrationRefreshDataHelpers';
import { trackAppRouteEvent } from '../actions/trackEvent';
import * as Analytics from '../utils/analytics';
import { selectIntegrationRefreshStatusForIntegrations } from '@/selectors/memoizedSelectors';
import { SettingType } from '@/types';

interface stateType {
    fromReleasePage: boolean;
}

const TimelineHeader = (props: AppComponentProps) => {
    const dispatch = useDispatch();
    const location = useLocation<stateType>();
    const history = useHistory();

    const isAppsDemoModeEnabled = useSelector((state: RootState) => selectIsAppsDemoModeEnabled(state));
    const combinedRefreshDataStatus = useSelector((state: RootState) => {
        return selectIntegrationRefreshStatusForIntegrations(state, props.appId);
    });

    const fromReleasePage = location.state && location.state.fromReleasePage;

    const { orgIdParam, appIdParam } = useParams<AppRouteComponentMatchParams>();
    const showBackArrow = useRouteMatch<ReleaseRouteComponentMatchParams>([routes.release, routes.appSettings]);
    const settingsMatch = useRouteMatch<ReleaseRouteComponentMatchParams>(routes.appSettings);
    const releaseMatch = useRouteMatch<ReleaseRouteComponentMatchParams>([routes.release]);

    const appSettingsRoute = createAppSettingsRouteWithSettingType(orgIdParam, appIdParam, SettingType.General);
    const appRoute = createAppRoute(orgIdParam, appIdParam);

    const [isOnReleasePage, setIsOnReleasePage] = React.useState(releaseMatch ? true : false);
    const [releaseRoute, setReleaseRoute] = React.useState(null);

    //need to save where user came from before clicking around the settings pages.
    const savedReleasePage = React.useRef<boolean>(false);

    const trackIntegrationsRefreshModuleOpened = () => {
        dispatch(
            trackAppRouteEvent({
                orgId: props.orgId,
                appId: props.appId,
                eventName: Analytics.Event.ViewIntegrationRefreshModule,
            }),
        );
    };

    React.useEffect(() => {
        if (releaseMatch) {
            setIsOnReleasePage(true);
            setReleaseRoute(location.pathname);
        } else {
            setIsOnReleasePage(false);
        }
        if (fromReleasePage) {
            savedReleasePage.current = true;
        } else if (settingsMatch && savedReleasePage.current === true) {
            //if user navigates between settings pages, but came from release page immediately prior
            savedReleasePage.current = true;
        } else {
            savedReleasePage.current = false;
        }
    }, [location]);

    React.useEffect(() => {
        //clear location.state so it doesn't persist after page refresh
        window.history.replaceState({}, document.title);
    }, []);

    const lastLocation = savedReleasePage.current === true ? releaseRoute : appRoute;

    const goBack = () => {
        history.replace(lastLocation);
    };

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                paddingLeft: 16,
                paddingRight: 16,
                paddingTop: 12,
                paddingBottom: 12,
                marginBottom: 8,
                backgroundColor: 'transparent',
            }}
        >
            <div
                style={{
                    display: 'flex',
                    minWidth: 0,
                }}
            >
                {showBackArrow && (
                    <div
                        style={{
                            marginRight: 8,
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        <Button
                            type="link"
                            icon={
                                <ArrowLeftOutlined
                                    style={{
                                        color: runwayColors.statusGrey,
                                        fontSize: 24,
                                    }}
                                />
                            }
                            onClick={goBack}
                        />
                        {/* {showBackHelper && (
                            <Text type="secondary" style={{ fontSize: 8 }}>
                                All releases
                            </Text>
                        )} */}
                    </div>
                )}
                <div
                    style={{
                        display: 'flex',
                        width: '100%',
                        position: 'relative',
                        justifyContent: settingsMatch ? 'flex-end' : 'space-between',
                        flexDirection: 'row',
                    }}
                >
                    {!settingsMatch && (
                        <div style={{ marginRight: 8 }}>
                            <Link
                                to={{
                                    pathname: appSettingsRoute,
                                    state: { fromReleasePage: isOnReleasePage },
                                }}
                            >
                                <Button
                                    type="link"
                                    icon={
                                        <SettingOutlined
                                            style={{
                                                color: runwayColors.statusGrey,
                                                fontSize: 24,
                                            }}
                                        />
                                    }
                                />
                            </Link>
                        </div>
                    )}
                    {!isAppsDemoModeEnabled && (
                        <div>
                            <div style={{ position: 'absolute', right: -8 }}>
                                {statusNodeForIntegrationStatus(combinedRefreshDataStatus)}
                            </div>
                            <Popover
                                placement="bottom"
                                arrow={{ pointAtCenter: true }}
                                content={<IntegrationRefreshPopover {...props} />}
                                overlayClassName="integration-refresh-popover"
                                destroyTooltipOnHide
                                onOpenChange={(open) => {
                                    if (open) {
                                        trackIntegrationsRefreshModuleOpened();
                                    }
                                }}
                            >
                                <Button
                                    type="link"
                                    icon={
                                        <CloudSyncOutlined
                                            style={{
                                                color: runwayColors.statusGrey,
                                                fontSize: 24,
                                            }}
                                        />
                                    }
                                />
                            </Popover>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TimelineHeader;
